import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../components/shared/Breadcrumb';
import { createTicket, TicketCategory, TicketPriority } from '../services/ticketsApi';
import { listMyOrders, Order } from '../services/ordersApi';
import '../styles/ReportIssuePage.css';

/**
 * VP-CUST-004 (ecommerce half): customer-facing "Report Issue" form.
 *
 * This app had zero support-ticket creation flow. The backend ticket-
 * creation endpoint (POST /tickets) fully supports the Customer role: it
 * auto-scopes customer_id + source=customer_portal server-side
 * (routes/tickets.ts), and accepts an optional order_id link. Mirrors
 * vin-pulse-os-web's CustomerReportIssue.tsx.
 */

const CUSTOMER_CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: 'customer', label: 'General / Account Issue' },
  { value: 'delivery', label: 'Delivery Issue' },
  { value: 'payment', label: 'Payment / Invoice Issue' },
  { value: 'returns', label: 'Return / Dispute' },
  { value: 'other', label: 'Other' },
];

const ReportIssuePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const presetCategory = searchParams.get('category') as TicketCategory | null;
  const presetOrderId = searchParams.get('orderId');
  const [category, setCategory] = useState<TicketCategory>(
    presetCategory && CUSTOMER_CATEGORIES.some((c) => c.value === presetCategory) ? presetCategory : 'customer'
  );
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [orderId, setOrderId] = useState<string>(presetOrderId ?? '');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    listMyOrders({ limit: 50 })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!subject.trim()) {
      setError('Please enter a subject.');
      return;
    }
    setSaving(true);
    try {
      const created = await createTicket({
        category,
        priority,
        subject: subject.trim(),
        description: description.trim() || undefined,
        order_id: orderId ? Number(orderId) : undefined,
      });
      setSuccess(`Ticket ${created.ticket_number} created — our team will follow up.`);
      setSubject('');
      setDescription('');
      setOrderId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="report-issue-page">
      <Breadcrumb />
      <div className="report-issue-page__container">
        <div className="report-issue-page__card">
          <h1 className="report-issue-page__title">Report an Issue</h1>
          <p className="report-issue-page__subtitle">Tell us what happened and our support team will get back to you.</p>

          <form className="report-issue-page__form" onSubmit={handleSubmit}>
            {error && <div className="report-issue-page__error">{error}</div>}
            {success && <div className="report-issue-page__success">{success}</div>}

            <div className="report-issue-page__field">
              <label className="report-issue-page__label" htmlFor="issue-category">Issue Type</label>
              <select
                id="issue-category"
                className="report-issue-page__input"
                value={category}
                onChange={(e) => setCategory(e.target.value as TicketCategory)}
              >
                {CUSTOMER_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="report-issue-page__field">
              <label className="report-issue-page__label" htmlFor="issue-order">Related Order (optional)</label>
              <select
                id="issue-order"
                className="report-issue-page__input"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
              >
                <option value="">None</option>
                {orders.map((o) => {
                  // GET /customers/me/orders returns a summary shape keyed by
                  // `order_id`, not `id` — same gotcha OrdersPage.tsx already
                  // works around.
                  const id = o.id ?? (o as any).order_id;
                  return (
                    <option key={id} value={id}>VIN-{id}</option>
                  );
                })}
              </select>
            </div>

            <div className="report-issue-page__field">
              <label className="report-issue-page__label" htmlFor="issue-priority">Priority</label>
              <select
                id="issue-priority"
                className="report-issue-page__input"
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="report-issue-page__field">
              <label className="report-issue-page__label" htmlFor="issue-subject">Subject *</label>
              <input
                id="issue-subject"
                className="report-issue-page__input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short summary of the issue"
                maxLength={200}
              />
            </div>

            <div className="report-issue-page__field">
              <label className="report-issue-page__label" htmlFor="issue-description">Description</label>
              <textarea
                id="issue-description"
                className="report-issue-page__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add any details that will help us resolve this quickly"
              />
            </div>

            <div className="report-issue-page__actions">
              <button type="button" className="report-issue-page__cancel-btn" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="report-issue-page__submit-btn" disabled={saving}>
                {saving ? 'Submitting…' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportIssuePage;
